import { useCallback, useRef } from 'react';
import { apiService, type Conversation as ApiConversation } from '../../../../services/api';
import type { ChatMessage } from '../../../ChatPanel';

/**
 * Hook for clearing conversation context/history
 */

interface UseClearContextProps {
  conversation: ApiConversation | null;
  messages: ChatMessage[];
  onMessagesUpdate: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  onConversationCleared?: () => Promise<void> | void;
  setError: (error: string | null) => void;
  setSuccessMessage: (message: string | null) => void;
}

export function useClearContext({
  conversation,
  messages,
  onMessagesUpdate,
  onConversationCleared,
  setError,
  setSuccessMessage,
}: UseClearContextProps) {
  const isDeletingRef = useRef(false);

  const handleClearContext = useCallback(async () => {
    if (isDeletingRef.current) return;
    isDeletingRef.current = true;

    try {
      let conversationId = conversation?.id;

      if (!conversationId) {
        if (messages.length > 0) {
          try {
            const conversationsResponse = await apiService.getConversations();
            if (conversationsResponse.conversations?.length > 0) {
              conversationId = conversationsResponse.conversations[0].id;
            }
          } catch (err) {
            console.error('Error getting conversations:', err);
          }
        } else {
          if (!conversation) {
            setError('No hay conversacion activa para limpiar');
            return;
          }
        }
      }

      if (!conversationId) {
        setError('No se pudo identificar la conversacion para limpiar el contexto');
        return;
      }

      try {
        const response = await apiService.deleteConversationMessages(conversationId);
        if (response.success) {
          onMessagesUpdate(() => []);
          setSuccessMessage('Historia borrada');
          if (onConversationCleared) {
            await onConversationCleared();
          }
          setTimeout(() => setSuccessMessage(null), 3000);
        }
      } catch (err: any) {
        console.error('Error clearing context:', err);
        setError('Error al limpiar el chat');
      }
    } finally {
      isDeletingRef.current = false;
    }
  }, [
    conversation,
    messages,
    onMessagesUpdate,
    onConversationCleared,
    setError,
    setSuccessMessage,
  ]);

  return { handleClearContext };
}
